
import Vue from 'vue';
import {
  UserModel,
  QuestionModel,
  ClassroomModel,
  CourseModel,
  ListModel,
} from '@descomplica/dex-models';
import { UnifiedCommentModel } from '@/models/UnifiedCommentModel';
import Content from '../components/contents/Content.vue';
import CommentCard from '../components/comment/CommentCard.vue';
import CommentInput from '../components/comment/CommentInput.vue';
import ContentAndSolutions from '../components/ContentAndSolutions.vue';
import Analytics from '@/helpers/analytics';
import { EVENT_LABELS } from '@/helpers/analytics/labels';
import { BaseTrackType } from '@/helpers/analytics/types';
import { ANALYTICS_LABELS } from '../helpers/listItems';
import { FileModel } from '../models/FileModel';
import { getCourseCategory } from '@/helpers/category';
import MicrocertificateRequestsService from '@/helpers/postgradMicrocertificate/request';
import { getDexToken } from '@/helpers/get-tokens';
import { shouldNotificateForNewMicrocertificate } from '@/helpers/postgradMicrocertificate/notificationCheck';
import { PostgradAnalytics } from '@/helpers/analytics/postgradAnalytics';
import { startOf } from '@/helpers/date';
import { UndergradAnalytics } from '@/helpers/analytics/undergradAnalytics';
import { ActivityMessage } from '@descomplica/vue-components';
import { UserListActivityStatus } from '@/services/UndergradGrades';
import DueDateWarningHelper, { ActivityMessageStatus } from '@/helpers/assessments/dueDateWarning';
import { TranslateResult } from 'vue-i18n';
import { ChimeraCourseModel } from '@/models/chimera/ChimeraCourseModel';
import { PostgradTrialStatus } from '@/models/postgradTrial/PostgradTrialModel';

export default Vue.extend({
  components: {
    Content,
    CommentCard,
    CommentInput,
    ContentAndSolutions,
    ActivityMessage,
  },
  props: {
    question: {
      type: Object as Vue.PropType<QuestionModel>,
      required: true,
    },
    comments: {
      type: Array as Vue.PropType<UnifiedCommentModel[]>,
      default: (): any[] => [],
    },
    classroom: {
      type: Object as Vue.PropType<ClassroomModel>,
      required: true,
    },
    listStatus: {
      type: Object as Vue.PropType<UserListActivityStatus>,
      default: null,
    },
  },
  data() {
    return {
      isSending: false,
    };
  },
  computed: {
    user(): UserModel {
      return this.$store.getters.currentUser;
    },
    course(): CourseModel & { progress: number } {
      return this.user.getCourse();
    },
    isPostgraduateCourse(): boolean {
      return this.$store.getters.isPostgraduateCourse;
    },
    userCanViewPostgradStudyPlanning(): boolean {
      return this.$store.getters.canViewPostgradStudyPlanning;
    },
    userHasActivePostgradStudyPlanning(): boolean {
      return Boolean(this.$store.getters.studyPlanning(this.$store.getters.currentCourse.id));
    },
    gradeActivitiesViaAi(): boolean {
      return this.$store.getters.gradeActivitiesViaAi;
    },
    currentList(): ListModel {
      return this.$store.getters.listBySlug(this.$route.params.listSlug);
    },
    currentClassroom(): ClassroomModel {
      return this.$store.getters.classroomBySlug(this.$route.params.classroomSlug);
    },
    currentChimeraCourse(): ChimeraCourseModel | null {
      return this.$store.getters.getCurrentChimeraCourse;
    },
    getTrialStatusByDescomplicaCourseId(): PostgradTrialStatus {
      return this.$store.getters.getTrialStatusByDescomplicaCourseId(
        this.currentChimeraCourse?._id,
      );
    },
    currentClassroomRoutePath(): string {
      const currentRoutePath = window.location.href;
      return currentRoutePath.replace(/\/aula\/\S*/, '');
    },
    currentUserComment(): UnifiedCommentModel | undefined {
      return (
        this.comments.find(
          (comment: UnifiedCommentModel) => comment.author && comment.author.id === this.user.id,
        ) || undefined
      );
    },
    classroomComments(): UnifiedCommentModel[] {
      if (!this.classroom) return [];
      const { studentsIds = [] } = this.classroom;
      return this.comments.filter(
        (comment) =>
          studentsIds.includes(comment.userId) &&
          (comment.author.id === this.user.id || this.isTeacher),
      );
    },
    hasCommented(): boolean {
      return !!this.currentUserComment;
    },
    isTeacher(): boolean {
      return this.user.isAdmin();
    },
    defaultTrackingProps(): BaseTrackType {
      return {
        course: this.course.name,
        discipline: this.currentClassroom.name,
        content_type: ANALYTICS_LABELS.trivia,
        category: getCourseCategory(this.course),
      };
    },
    isUndergraduateCourse(): boolean {
      return this.$store.getters.isUndergraduateCourse;
    },
  },
  mounted() {
    this.trackingContentOpened();

    if (this.listStatus) this.trackingActivityMessageView();
  },
  methods: {
    async submit(params: { text: string; files: FileModel[] }): Promise<void | undefined> {
      if (this.isSending) return;

      const { text } = params;
      if (text) {
        this.isSending = true;
        const isGradeActivitiesViaAi = this.gradeActivitiesViaAi;
        const oldClassroomProgress = this.currentClassroom.progress;

        await this.$store
          .dispatch('CREATE_THINK_AND_ANSWER_COMMENT', {
            questionId: this.question.id,
            text,
            isGradeActivitiesViaAi,
          })
          .finally(async () => {
            this.isSending = false;

            await this.$store.dispatch('PULL_CLASSROOMS_PROGRESS', {
              classroomIds: [this.currentClassroom.id],
            });

            const updatedClassroomProgress = this.currentClassroom.progress;

            if (this.isPostgraduateCourse && this.user.email) {
              this.handlePostgradClassroomProgress(
                oldClassroomProgress ?? 0,
                updatedClassroomProgress ?? 0,
              );
            }
          });

        this.trackingContentConsumed();
      }
    },
    updateScore(comment: UnifiedCommentModel): void {
      this.$store.dispatch('UPDATE_COMMENT_SCORE', comment);
    },
    trackingContentOpened(): void {
      Analytics.trackEvent(EVENT_LABELS.contentOpened, {
        ...this.defaultTrackingProps,
        lesson_slug: this.currentList.slug,
        lesson_name: this.currentList.name,
      });
    },
    trackingContentConsumed(): void {
      Analytics.trackEvent(EVENT_LABELS.contentConsumed, {
        ...this.defaultTrackingProps,
        lesson_slug: this.currentList.slug,
        lesson_name: this.currentList.name,
        content_status: 'answered',
        content_id: this.question.id,
        postgrad_study_planning_feature_enabled: this.userCanViewPostgradStudyPlanning,
        has_postgrad_study_planning: this.userHasActivePostgradStudyPlanning,
        ...this.getTrialStatusByDescomplicaCourseId,
      });

      if (this.isPostgraduateCourse) {
        PostgradAnalytics.updateHubSpotContactProperties({
          last_trivia_answered_date: startOf(new Date()).getTime(),
        });
      }

      if (this.isUndergraduateCourse) {
        UndergradAnalytics.updateHubSpotContactProperties({
          last_trivia_answered_date: startOf(new Date()).getTime(),
        });
      }
    },
    trackingActivityMessageView(): void {
      Analytics.trackEvent(EVENT_LABELS.activityDeadlineView, {
        course: this.$store.getters.currentCourse.name,
        category: getCourseCategory(this.$store.getters.currentCourse),
        content_type: ANALYTICS_LABELS.trivia,
        lesson_slug: this.$route.params.listSlug,
        discipline_slug: this.$route.params.classroomSlug,
        activity_status: this.listStatus.status,
      });
    },
    handlePostgradClassroomProgress(
      oldClassroomProgress: number,
      updatedClassroomProgress: number,
    ): void {
      if (
        !this.isPostgraduateCourse ||
        !shouldNotificateForNewMicrocertificate(oldClassroomProgress, updatedClassroomProgress)
      )
        return;

      MicrocertificateRequestsService.handleClassroomProgress(getDexToken()!, {
        email: this.user.email!,
        oldClassroomProgress,
        classroomName: this.currentClassroom.name,
        classroomId: this.currentClassroom.id,
        classroomUrl: this.currentClassroomRoutePath,
      });
    },
    activityStatus(): ActivityMessageStatus {
      return DueDateWarningHelper.getActivityStatus(this.listStatus.status);
    },
    warningMessage(): TranslateResult {
      return DueDateWarningHelper.getTriviaWarningMessage(
        this.listStatus.status,
        this.isPastDue(),
        this.listStatus.dueDate,
      );
    },
    isPastDue(): boolean {
      const { answeredAt, dueDate, status } = this.listStatus || {};
      return DueDateWarningHelper.isPastDue(dueDate, answeredAt, status);
    },
  },
});
